import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { APISpecialtyForm } from 'types/api.types';

// Helper function to calculate total with proper decimal precision
const calculateTotal = (
  premium: number,
  companyFees: number,
  brokerFees: number,
  taxes: number,
): number => {
  return Math.round((premium + companyFees + brokerFees + taxes) * 100) / 100;
};

export interface SpecialtyFormState {
  data: APISpecialtyForm;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  validationErrors: Record<string, any>;
  isDataSet: boolean;
  hasUnsavedChanges: boolean;
  wipStatus: number | null;
  lineOfBusiness: 'Auto' | 'Home' | null;
  premiumConfirmed: boolean;
}

const initialState: SpecialtyFormState = {
  data: {
    insurance_company: '',
    effective_date: '',
    binder_policy_number: '',
    business_type: 'new',
    premium: 0,
    client_name: '',
    customer_code: '',
    notes_to_underwriter: '',
    policy_information: {
      is_loa: false,
      loa_prior_broker: '',
      loa_prior_policy_number: '',
    },
    billing: {
      include_billing: false,
      payment_schedule: '',
      payment_method: 'agency_bill',
      province: '',
      business_code: '',
      company_fees: 0,
      broker_fees: 0,
      taxes: 0,
      total: 0,
    },
    disclosure_and_attestation: {
      broker_name: '',
    },
  },
  isLoading: false,
  isSaving: false,
  error: null,
  validationErrors: {},
  isDataSet: false,
  hasUnsavedChanges: false,
  wipStatus: null,
  lineOfBusiness: null,
  premiumConfirmed: false,
};

const specialtyFormSlice = createSlice({
  name: 'specialtyForm',
  initialState,
  reducers: {
    setInsuranceCompany: (state, action: PayloadAction<string>) => {
      state.data.insurance_company = action.payload;
      state.hasUnsavedChanges = true;
    },
    setNotesToUnderwriter: (state, action: PayloadAction<string>) => {
      state.data.notes_to_underwriter = action.payload;
      state.hasUnsavedChanges = true;
    },
    setEffectiveDate: (state, action: PayloadAction<string>) => {
      state.data.effective_date = action.payload;
      state.hasUnsavedChanges = true;
    },
    setBinderPolicyNumber: (state, action: PayloadAction<string>) => {
      state.data.binder_policy_number = action.payload;
      state.hasUnsavedChanges = true;
    },
    setBusinessType: (state, action: PayloadAction<'new' | 'remarket'>) => {
      state.data.business_type = action.payload;
      state.hasUnsavedChanges = true;
    },
    setPremium: (state, action: PayloadAction<number>) => {
      state.data.premium = action.payload;
      state.data.billing.total = calculateTotal(
        state.data.premium,
        state.data.billing.company_fees,
        state.data.billing.broker_fees,
        state.data.billing.taxes,
      );
      // Reset premium confirmation if premium drops to or below threshold
      if (action.payload <= 10000) {
        state.premiumConfirmed = false;
      }
      state.hasUnsavedChanges = true;
    },
    setPremiumConfirmed: (state, action: PayloadAction<boolean>) => {
      state.premiumConfirmed = action.payload;
    },
    setClientName: (state, action: PayloadAction<string>) => {
      state.data.client_name = action.payload;
      state.hasUnsavedChanges = true;
    },
    setCustomerCode: (state, action: PayloadAction<string>) => {
      state.data.customer_code = action.payload;
      state.hasUnsavedChanges = true;
    },
    setLOAPriorBroker: (state, action: PayloadAction<string>) => {
      state.data.policy_information.loa_prior_broker = action.payload;
      state.hasUnsavedChanges = true;
    },
    setLOAPriorPolicyNumber: (state, action: PayloadAction<string>) => {
      state.data.policy_information.loa_prior_policy_number = action.payload;
      state.hasUnsavedChanges = true;
    },
    setIncludeBilling: (state, action: PayloadAction<boolean>) => {
      state.data.billing.include_billing = action.payload;
      state.hasUnsavedChanges = true;
    },
    setPaymentSchedule: (state, action: PayloadAction<string>) => {
      state.data.billing.payment_schedule = action.payload;
      state.hasUnsavedChanges = true;
    },
    setPaymentMethod: (
      state,
      action: PayloadAction<'agency_bill' | 'direct_bill' | 'eft_credit_card'>,
    ) => {
      state.data.billing.payment_method = action.payload;
      state.hasUnsavedChanges = true;
    },
    setBillingProvince: (state, action: PayloadAction<string>) => {
      state.data.billing.province = action.payload;
      state.hasUnsavedChanges = true;
    },
    setBusinessCode: (state, action: PayloadAction<string>) => {
      state.data.billing.business_code = action.payload;
      state.hasUnsavedChanges = true;
    },
    setCompanyFees: (state, action: PayloadAction<number>) => {
      state.data.billing.company_fees = action.payload;
      state.data.billing.total = calculateTotal(
        state.data.premium,
        state.data.billing.company_fees,
        state.data.billing.broker_fees,
        state.data.billing.taxes,
      );
      state.hasUnsavedChanges = true;
    },
    setBrokerFees: (state, action: PayloadAction<number>) => {
      state.data.billing.broker_fees = action.payload;
      state.data.billing.total = calculateTotal(
        state.data.premium,
        state.data.billing.company_fees,
        state.data.billing.broker_fees,
        state.data.billing.taxes,
      );
      state.hasUnsavedChanges = true;
    },
    setTaxes: (state, action: PayloadAction<number>) => {
      state.data.billing.taxes = action.payload;
      state.data.billing.total = calculateTotal(
        state.data.premium,
        state.data.billing.company_fees,
        state.data.billing.broker_fees,
        state.data.billing.taxes,
      );
      state.hasUnsavedChanges = true;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setSaving: (state, action: PayloadAction<boolean>) => {
      state.isSaving = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setValidationErrors: (
      state,
      action: PayloadAction<Record<string, any>>,
    ) => {
      state.validationErrors = action.payload;
    },
    clearValidationErrors: (state) => {
      state.validationErrors = {};
    },
    setDataSet: (state, action: PayloadAction<boolean>) => {
      state.isDataSet = action.payload;
    },
    setHasUnsavedChanges: (state, action: PayloadAction<boolean>) => {
      state.hasUnsavedChanges = action.payload;
    },
    setWipStatus: (state, action: PayloadAction<number | null>) => {
      state.wipStatus = action.payload;
    },
    setLineOfBusiness: (
      state,
      action: PayloadAction<'Auto' | 'Home' | null>,
    ) => {
      state.lineOfBusiness = action.payload;
    },
    changeDisclosureAndAttestation: (
      state,
      action: PayloadAction<{
        name: string;
        value: any;
      }>,
    ) => {
      state.data.disclosure_and_attestation = {
        ...state.data.disclosure_and_attestation,
        [action.payload.name]: action.payload.value,
      };
      state.hasUnsavedChanges = true;
    },
    resetForm: (state) => {
      // Preserve the lineOfBusiness when resetting
      const preservedLineOfBusiness = state.lineOfBusiness;
      return {
        ...initialState,
        lineOfBusiness: preservedLineOfBusiness,
      };
    },
    initializeForm: (
      state,
      action: PayloadAction<Partial<APISpecialtyForm>>,
    ) => {
      state.data = {
        insurance_company: action.payload.insurance_company || '',
        effective_date: action.payload.effective_date || '',
        binder_policy_number: action.payload.binder_policy_number || '',
        business_type: action.payload.business_type || 'new',
        premium: action.payload.premium || 0,
        client_name: action.payload.client_name || '',
        customer_code: action.payload.customer_code || '',
        notes_to_underwriter: action.payload.notes_to_underwriter || '',
        policy_information: {
          is_loa: action.payload.policy_information?.is_loa || false,
          loa_prior_broker:
            action.payload.policy_information?.loa_prior_broker || '',
          loa_prior_policy_number:
            action.payload.policy_information?.loa_prior_policy_number || '',
        },
        billing: {
          include_billing: action.payload.billing?.include_billing || false,
          payment_schedule: action.payload.billing?.payment_schedule || '',
          payment_method:
            action.payload.billing?.payment_method || 'agency_bill',
          province: action.payload.billing?.province || '',
          business_code: action.payload.billing?.business_code || '',
          company_fees: action.payload.billing?.company_fees || 0,
          broker_fees: action.payload.billing?.broker_fees || 0,
          taxes: action.payload.billing?.taxes || 0,
          // Recalculate total to fix any floating-point precision errors
          total: calculateTotal(
            action.payload.premium || 0,
            action.payload.billing?.company_fees || 0,
            action.payload.billing?.broker_fees || 0,
            action.payload.billing?.taxes || 0,
          ),
        },
        disclosure_and_attestation: {
          broker_name:
            action.payload.disclosure_and_attestation?.broker_name || '',
        },
      };
      state.wipStatus = action.payload.wip_status || null;
      state.premiumConfirmed = action.payload.premium_confirmed || false;
      state.isDataSet = true;
      state.hasUnsavedChanges = false;
      state.error = null;
      state.validationErrors = {};
    },
  },
});

export const {
  setInsuranceCompany,
  setNotesToUnderwriter,
  setEffectiveDate,
  setBinderPolicyNumber,
  setBusinessType,
  setPremium,
  setPremiumConfirmed,
  setClientName,
  setCustomerCode,
  setLOAPriorBroker,
  setLOAPriorPolicyNumber,
  setIncludeBilling,
  setPaymentSchedule,
  setPaymentMethod,
  setBillingProvince,
  setBusinessCode,
  setCompanyFees,
  setBrokerFees,
  setTaxes,
  setLoading,
  setSaving,
  setError,
  setValidationErrors,
  clearValidationErrors,
  setDataSet,
  setHasUnsavedChanges,
  setWipStatus,
  setLineOfBusiness,
  changeDisclosureAndAttestation,
  resetForm,
  initializeForm,
} = specialtyFormSlice.actions;

export default specialtyFormSlice.reducer;
